import { useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useBranchContext } from "@/contexts/BranchContext";
import { useDemo } from "@/contexts/DemoContext";
import { useDemoStoreOptional } from "@/contexts/DemoStoreContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/utils";
import type { MaintenanceRecord, MaintenanceType, TaskStatus } from "@/types/maintenance";

const KM_FIELD_MAP: Partial<Record<MaintenanceType, keyof TablesUpdate<"vehicles">>> = {
  oil_change: "last_lube_change_km",
  tire_rotation: "last_tire_change_km",
  brake_service: "last_brake_service_km",
  engine_repair: "last_engine_check_km",
};

type MaintenanceMutation = Partial<MaintenanceRecord> & { id?: string };
type MaintenanceRow = Tables<"maintenance"> & {
  vehicles:
    | (NonNullable<MaintenanceRecord["vehicle"]> & { branch_id?: string | null })
    | null;
};
type MaintenanceInsertPayload = TablesInsert<"maintenance">;
type MaintenanceUpdatePayload = TablesUpdate<"maintenance">;
type AdvanceStatusInput = {
  id: string;
  nextStatus: TaskStatus;
  notes?: string;
  photos?: string[];
};

export function useMaintenance() {
  const { isDemo } = useDemo();
  const store = useDemoStoreOptional();
  const { profile } = useAuth();
  const { toast } = useToast();
  const { activeBranchId } = useBranchContext();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["maintenance", profile?.business_id, activeBranchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("maintenance")
        .select("*, vehicles!maintenance_vehicle_id_fkey(id, make, model, plate_number, mileage, branch_id)")
        .eq("business_id", profile!.business_id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return ((data ?? []) as MaintenanceRow[]).map((record): MaintenanceRecord => ({
        id: record.id,
        vehicle_id: record.vehicle_id ?? "",
        maintenance_type: (record.maintenance_type ?? "other") as MaintenanceType,
        title: record.title ?? null,
        issue_type: record.issue_type ?? null,
        severity: (record.severity as MaintenanceRecord["severity"]) ?? "medium",
        reported_by_name: record.reported_by_name ?? null,
        mileage_at_report: record.mileage_at_report ?? null,
        technician_name: record.technician_name ?? null,
        technician_phone: record.technician_phone ?? null,
        vendor_garage: record.vendor_garage ?? null,
        estimated_downtime: record.estimated_downtime ?? null,
        booking_id: record.booking_id ?? null,
        description: record.description,
        cost: record.cost,
        completed: record.completed ?? false,
        auto_generated: !!record.auto_generated,
        created_at: record.created_at ?? new Date().toISOString(),
        status: record.status,
        priority: record.priority,
        assigned_to: record.assigned_to ?? null,
        acknowledged_at: record.acknowledged_at ?? null,
        started_at: record.started_at ?? null,
        completed_at: record.completed_at ?? null,
        proof_photos: Array.isArray(record.proof_photos)
          ? record.proof_photos.filter((photo): photo is string => typeof photo === "string")
          : [],
        completion_notes: record.completion_notes ?? null,
        reviewed_by: record.reviewed_by ?? null,
        reviewed_at: record.reviewed_at ?? null,
        due_date: record.due_date ?? null,
        escalated: record.escalated ?? false,
        escalated_at: record.escalated_at ?? null,
        vehicle: record.vehicles ?? undefined,
        assigned_profile: null,
      }));
    },
    enabled: !isDemo && !!profile?.business_id,
  });

  const allRecords = useMemo<MaintenanceRecord[]>(
    () => (isDemo ? (store?.maintenance ?? []) : (query.data ?? [])),
    [isDemo, query.data, store?.maintenance],
  );

  const records = activeBranchId
    ? allRecords.filter((record) => record.vehicle?.branch_id === activeBranchId)
    : allRecords;

  useEffect(() => {
    if (isDemo || !allRecords.length || !profile?.business_id) return;

    const now = new Date();
    const overdue = allRecords.filter(
      (task) =>
        !task.escalated &&
        !!task.due_date &&
        new Date(task.due_date) < now &&
        (task.status === "open" || task.status === "in_progress"),
    );

    if (!overdue.length) return;

    const escalate = async () => {
      const ids = overdue.map((task) => task.id);
      const { data: existing } = await supabase
        .from("alerts")
        .select("reference_id")
        .eq("alert_type", "task_escalation")
        .eq("resolved", false)
        .in("reference_id", ids);

      const alreadyAlerting = new Set((existing ?? []).map((alert) => alert.reference_id));

      for (const task of overdue) {
        await supabase
          .from("maintenance")
          .update({ escalated: true, escalated_at: new Date().toISOString() })
          .eq("id", task.id);

        if (!alreadyAlerting.has(task.id)) {
          await supabase.from("alerts").insert({
            business_id: profile.business_id,
            alert_type: "task_escalation",
            title: "Overdue Maintenance Task",
            message: `${task.title || task.maintenance_type} on ${task.vehicle?.make} ${task.vehicle?.model} â€” overdue since ${task.due_date}`,
            severity: "warning",
            reference_id: task.id,
            reference_type: "maintenance",
            is_read: false,
            resolved: false,
          });
        }
      }

      qc.invalidateQueries({ queryKey: ["maintenance"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    };

    void escalate();
  }, [allRecords, isDemo, profile?.business_id, qc]);

  const upsertMaintenance = useMutation({
    mutationFn: async (record: MaintenanceMutation) => {
      if (isDemo && store) {
        return store.upsertMaintenance(record);
      }

      const { id, vehicle, assigned_profile, auto_generated, ...rest } = record;
      const payload = {
        ...rest,
        business_id: profile?.business_id ?? null,
      } as MaintenanceInsertPayload;

      if (id) {
        const { data, error } = await supabase
          .from("maintenance")
          .update(payload)
          .eq("id", id)
          .select()
          .single();

        if (error) throw error;
        return data;
      }

      const { data, error } = await supabase
        .from("maintenance")
        .insert(payload)
        .select()
        .single();

      if (error) throw error;

      if (data?.vehicle_id && payload.severity) {
        if (payload.severity === "critical") {
          await supabase.from("vehicles").update({ status: "blocked" }).eq("id", data.vehicle_id);
        } else if (payload.severity === "high") {
          await supabase
            .from("vehicles")
            .update({ status: "maintenance" })
            .eq("id", data.vehicle_id)
            .eq("status", "available");
        }
      }

      return data;
    },
    onSuccess: () => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["maintenance"] });
        qc.invalidateQueries({ queryKey: ["vehicles"] });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const deleteMaintenance = useMutation({
    mutationFn: async (id: string) => {
      if (isDemo && store) {
        store.deleteMaintenance(id);
        return;
      }

      const { error } = await supabase.from("maintenance").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["maintenance"] });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const toggleComplete = useMutation({
    mutationFn: async ({ id, completed }: { id: string; completed: boolean }) => {
      if (isDemo && store) {
        store.toggleMaintenanceComplete(id, completed);
        return;
      }

      const { error } = await supabase
        .from("maintenance")
        .update({ completed, status: completed ? "closed" : "open" })
        .eq("id", id);

      if (error) throw error;

      if (completed) {
        const { data: task } = await supabase
          .from("maintenance")
          .select("vehicle_id")
          .eq("id", id)
          .single();

        if (task?.vehicle_id) {
          await supabase
            .from("vehicles")
            .update({ status: "available" })
            .eq("id", task.vehicle_id)
            .in("status", ["maintenance", "blocked"]);
        }
      }
    },
    onSuccess: () => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["maintenance"] });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const advanceStatus = useMutation({
    mutationFn: async ({ id, nextStatus, notes, photos }: AdvanceStatusInput) => {
      const now = new Date().toISOString();
      const update: MaintenanceUpdatePayload = { status: nextStatus };

      if (nextStatus === "in_progress") {
        update.started_at = now;
      }

      if (nextStatus === "review" || nextStatus === "closed") {
        update.completed_at = now;
        if (notes) update.completion_notes = notes;
        if (photos) update.proof_photos = photos;
      }

      if (nextStatus === "closed") {
        update.reviewed_at = now;
        update.completed = true;
        update.reviewed_by = profile?.id ?? null;
      }

      if (nextStatus === "rejected") {
        update.reviewed_at = now;
        update.completed = false;
        update.reviewed_by = profile?.id ?? null;
      }

      if (isDemo && store) {
        store.upsertMaintenance({ id, ...update });

        if (nextStatus === "closed") {
          const task = store.maintenance.find((maintenance) => maintenance.id === id);
          if (task?.vehicle_id) {
            const vehicle = store.vehicles.find((storeVehicle) => storeVehicle.id === task.vehicle_id);
            if (vehicle?.status === "maintenance" || vehicle?.status === "blocked") {
              store.upsertVehicle({ id: task.vehicle_id, status: "available" });
            }
          }
        }

        return;
      }

      const { error } = await supabase.from("maintenance").update(update).eq("id", id);
      if (error) throw error;

      if (nextStatus === "closed") {
        const { data: task } = await supabase
          .from("maintenance")
          .select("vehicle_id, maintenance_type")
          .eq("id", id)
          .single();

        if (task?.vehicle_id) {
          await supabase
            .from("vehicles")
            .update({ status: "available" })
            .eq("id", task.vehicle_id)
            .in("status", ["maintenance", "blocked"]);

          const maintenanceType = task.maintenance_type as MaintenanceType | null;
          const kmField = maintenanceType ? KM_FIELD_MAP[maintenanceType] : undefined;

          if (kmField) {
            const { data: vehicle } = await supabase
              .from("vehicles")
              .select("mileage")
              .eq("id", task.vehicle_id)
              .single();

            if (vehicle?.mileage != null) {
              const vehicleUpdate = { [kmField]: vehicle.mileage } as TablesUpdate<"vehicles">;
              await supabase.from("vehicles").update(vehicleUpdate).eq("id", task.vehicle_id);
            }
          }
        }
      }
    },
    onSuccess: (_, variables) => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["maintenance"] });
        qc.invalidateQueries({ queryKey: ["vehicles"] });

        if (variables.nextStatus === "closed" || variables.nextStatus === "rejected") {
          qc.invalidateQueries({ queryKey: ["bookings"] });
          qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
        }
      }

      const labels: Record<TaskStatus, string> = {
        open: "Reopened",
        in_progress: "Work started",
        review: "Submitted for review",
        closed: "Job reviewed and accepted",
        rejected: "Job rejected",
      };

      toast({ title: labels[variables.nextStatus] ?? "Status updated" });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const assignTask = useMutation({
    mutationFn: async ({ id, assignedTo, dueDate }: { id: string; assignedTo: string; dueDate?: string }) => {
      const update: MaintenanceUpdatePayload = {
        assigned_to: assignedTo,
        escalated: false,
        escalated_at: null,
      };

      if (dueDate) {
        update.due_date = dueDate;
      }

      const { error } = await supabase.from("maintenance").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      toast({ title: "Task assigned" });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const saveTechnicianInfo = useMutation({
    mutationFn: async ({ id, technicianName, technicianPhone, dueDate }: {
      id: string;
      technicianName: string;
      technicianPhone: string;
      dueDate?: string;
    }) => {
      if (isDemo && store) {
        store.upsertMaintenance({
          id,
          technician_name: technicianName || null,
          technician_phone: technicianPhone || null,
          due_date: dueDate || null,
        });
        return;
      }

      const update: MaintenanceUpdatePayload = {
        technician_name: technicianName || null,
        technician_phone: technicianPhone || null,
      };

      if (dueDate !== undefined) {
        update.due_date = dueDate || null;
      }

      const { error } = await supabase.from("maintenance").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["maintenance"] });
      }
      toast({ title: "Technician info saved" });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const acknowledgeTask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("maintenance").update({
        status: "in_progress",
        started_at: new Date().toISOString(),
      }).eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      toast({ title: "Work started" });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const startTask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("maintenance").update({
        status: "in_progress",
        started_at: new Date().toISOString(),
      }).eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      toast({ title: "Work started" });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const completeTask = useMutation({
    mutationFn: async ({ id, notes, photos }: { id: string; notes?: string; photos?: string[] }) => {
      const update: MaintenanceUpdatePayload = {
        status: "review",
        completed_at: new Date().toISOString(),
      };

      if (notes) update.completion_notes = notes;
      if (photos) update.proof_photos = photos;

      const { error } = await supabase.from("maintenance").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      toast({ title: "Submitted for review" });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const reviewTask = useMutation({
    mutationFn: async ({ id, approved }: { id: string; approved: boolean }) => {
      await advanceStatus.mutateAsync({ id, nextStatus: approved ? "closed" : "rejected" });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  return {
    records,
    isLoading: !isDemo && query.isLoading,
    error: query.error,
    upsertMaintenance,
    deleteMaintenance,
    toggleComplete,
    advanceStatus,
    assignTask,
    saveTechnicianInfo,
    acknowledgeTask,
    startTask,
    completeTask,
    reviewTask,
  };
}
